import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { SelectionValuesType } from "components/dashboard/product-template-table/utils/interfaces";
import { T } from "services/lang/LanguageService";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { CellSubType } from "utils/enums";
import { Button } from "primereact/button";
import { formatTimeHHmm, toZonedDateTime } from "utils/time-utils";
import { Instant } from "@js-joda/core";
import { useDisplayTimezone } from "hooks/useTimezone";
import EditableCell from "./EditableCell";
import TimeValueEditor, { Item } from "../time/TimeValueEditor";

interface ChipsEditorProps {
  selectionValues?: SelectionValuesType;
  items: Item[];
  subType: CellSubType;
  onChange: (updater: (prev: Item[]) => Item[]) => void;
}

const ChipsEditor = ({ items, subType, onChange, selectionValues }: ChipsEditorProps) => {
  const displayTz = useDisplayTimezone();
  const stopRowInteraction = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addNew = (e: React.SyntheticEvent) => {
    stopRowInteraction(e);
    let newValue = "";
    if (subType === CellSubType.TIME) {
      newValue = formatTimeHHmm(toZonedDateTime(Instant.now(), displayTz));
    }
    onChange(prev => [...prev, { id: uuidv4(), value: newValue }]);
  };

  const removeRow = (e: React.SyntheticEvent, rowData: Item) => {
    stopRowInteraction(e);
    onChange(prev => prev.filter(item => item.id !== rowData.id));
  };

  const valueBodyTemplate = (rowData: Item) => {
    if (subType === CellSubType.TIME) {
      return (
        <TimeValueEditor
          data={rowData}
          onCommit={newVal =>
            onChange(prev => prev.map(item => (item.id === rowData.id ? { ...item, value: newVal } : item)))
          }
        />
      );
    }

    return (
      <EditableCell
        value={rowData.value}
        subType={subType}
        selectionValues={selectionValues}
        onCommit={newVal =>
          onChange(prev => prev.map(item => (item.id === rowData.id ? { ...item, value: newVal } : item)))
        }
      />
    );
  };

  const removeButtonTemplate = (rowData: Item) => (
    <Button
      icon="pi pi-times"
      className="p-button-text p-button-danger"
      onClick={e => removeRow(e, rowData)}
    />
  );

  const addButtonTemplate = () => {
    return (
      <Button
        label={T("action.add")}
        icon="pi pi-plus"
        className="component-editor-button icon-green"
        onClick={addNew}
      />
    );
  };

  return (
    <DataTable
      size="small"
      value={items}
      dataKey="id"
      reorderableRows
      onRowReorder={e => onChange(() => e.value)}
    >
      <Column rowReorder style={{ width: "10%" }} />
      <Column field="value" header={T("word.value")} style={{ width: "80%" }} body={valueBodyTemplate} />
      <Column
        header={addButtonTemplate}
        style={{ width: "10%", textAlign: "end" }}
        body={removeButtonTemplate}
      />
    </DataTable>
  );
};

export default ChipsEditor;
